import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Icon from "@mui/material/Icon";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDAlert from "components/MDAlert";
import ImageUpload from "components/ImageUpload";
import RichTextEditor from "components/RichTextEditor";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

import { adminApi } from "services/api";
import { lightDark, DARK } from "utils/lightDark";
import { buildBootcampPayload } from "./bootcampPayload";

// Below md the DataTable shows each row as a card, which would print long text in full; clamp it
// there (column `clampLines`, default 3) so cards stay scannable. Desktop cells are unchanged.
function ClampedCell({ value, column }) {
  return (
    <MDBox
      component="span"
      color="inherit"
      sx={({ breakpoints }) => ({
        [breakpoints.down("md")]: {
          display: "-webkit-box",
          WebkitLineClamp: column.clampLines || 3,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        },
      })}
    >
      {value}
    </MDBox>
  );
}

ClampedCell.propTypes = {
  value: PropTypes.node,
  column: PropTypes.shape({ clampLines: PropTypes.number }).isRequired,
};

// Full-screen dialog (below sm): let the form content scroll between a pinned title and action bar.
const fullScreenForm = { display: "flex", flexDirection: "column", flex: "1 1 auto", minHeight: 0 };

// Small buttons are 32px tall; lift them to a comfortable tap size on touch screens.
const phoneTapHeight = ({ breakpoints }) => ({ [breakpoints.down("md")]: { minHeight: 36 } });

function Bootcamps() {
  const [bootcamps, setBootcamps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // Save failures show inside the open dialog, not on the page behind it.
  const [dialogError, setDialogError] = useState("");
  const [success, setSuccess] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedBootcamp, setSelectedBootcamp] = useState(null);
  const theme = useTheme();
  const fullScreenDialog = useMediaQuery(theme.breakpoints.down("sm"));

  const [formData, setFormData] = useState({
    title: "",
    slug: "",
    description: "",
    duration: "",
    price: "",
    startDate: "",
    capacity: "",
    image: "",
    active: true,
    showOnMenu: true,
    level: "BEGINNER",
    classSize: "",
    format: "ONLINE",
    certificate: true,
    curriculum: [],
    prerequisites: [],
    whatsIncluded: [],
    additionalSupport: [],
    instructors: [],
    jobPlacementRate: "",
    averageStartingSalary: "",
    averageTimeToHire: "",
  });

  useEffect(() => {
    fetchBootcamps();
  }, []);

  const fetchBootcamps = async () => {
    try {
      setLoading(true);
      const data = await adminApi.getBootcamps();
      setBootcamps(data);
      setError("");
    } catch (err) {
      setError(
        err.response?.data?.error || err.response?.data?.message || "Failed to fetch bootcamps"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = () => {
    setEditMode(false);
    setFormData({
      title: "",
      slug: "",
      description: "",
      duration: "",
      price: "",
      startDate: "",
      capacity: "",
      image: "",
      active: true,
      showOnMenu: true,
      level: "BEGINNER",
      classSize: "",
      format: "ONLINE",
      certificate: true,
      curriculum: [],
      prerequisites: [],
      whatsIncluded: [],
      additionalSupport: [],
      instructors: [],
      jobPlacementRate: "",
      averageStartingSalary: "",
      averageTimeToHire: "",
    });
    setDialogError("");
    setOpenDialog(true);
  };

  const handleEdit = (bootcamp) => {
    setEditMode(true);
    setCurrentId(bootcamp._id);
    setFormData({
      title: bootcamp.title,
      slug: bootcamp.slug,
      description: bootcamp.description,
      duration: bootcamp.duration || "",
      price: bootcamp.price ?? "",
      startDate: bootcamp.startDate ? bootcamp.startDate.split("T")[0] : "",
      capacity: bootcamp.maxStudents ?? "",
      image: bootcamp.image || "",
      active: bootcamp.active !== undefined ? bootcamp.active : true,
      showOnMenu: bootcamp.showOnMenu !== undefined ? !!bootcamp.showOnMenu : true,
      level: bootcamp.level || "BEGINNER",
      classSize: bootcamp.classSize || "",
      format: bootcamp.format || "ONLINE",
      certificate: bootcamp.certificate !== undefined ? bootcamp.certificate : true,
      curriculum: bootcamp.curriculum || [],
      prerequisites: bootcamp.prerequisites || [],
      whatsIncluded: bootcamp.whatsIncluded || [],
      additionalSupport: bootcamp.additionalSupport || [],
      instructors: (bootcamp.instructors || []).map((instructor) => ({ ...instructor })),
      jobPlacementRate: bootcamp.jobPlacementRate ?? "",
      averageStartingSalary: bootcamp.averageStartingSalary ?? "",
      averageTimeToHire: bootcamp.averageTimeToHire ?? "",
    });
    setDialogError("");
    setOpenDialog(true);
    handleMenuClose();
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditMode(false);
    setCurrentId(null);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === "checkbox" ? checked : value;

    setFormData((prev) => {
      const updated = {
        ...prev,
        [name]: newValue,
      };

      // Auto-generate slug from title for new bootcamps
      if (name === "title" && !editMode) {
        updated.slug = value
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/(^-|-$)/g, "");
      }

      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setDialogError("");
    setError("");
    setSuccess("");

    try {
      // Trim list fields and send Capacity as the model's maxStudents
      const cleanedData = buildBootcampPayload(formData);

      if (editMode) {
        await adminApi.updateBootcamp(currentId, cleanedData);
        setSuccess("Bootcamp updated successfully");
      } else {
        await adminApi.createBootcamp(cleanedData);
        setSuccess("Bootcamp created successfully");
      }
      handleCloseDialog();
      fetchBootcamps();
    } catch (err) {
      setDialogError(
        err.response?.data?.error || err.response?.data?.message || "Operation failed"
      );
    }
  };

  const handleDelete = async () => {
    if (!selectedBootcamp) return;

    try {
      await adminApi.deleteBootcamp(selectedBootcamp._id);
      setSuccess("Bootcamp deleted successfully");
      fetchBootcamps();
      handleMenuClose();
    } catch (err) {
      setError(
        err.response?.data?.error || err.response?.data?.message || "Failed to delete bootcamp"
      );
    }
  };

  const handleMenuOpen = (event, bootcamp) => {
    setAnchorEl(event.currentTarget);
    setSelectedBootcamp(bootcamp);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedBootcamp(null);
  };

  const columns = [
    { Header: "title", accessor: "title", width: "25%", align: "left", Cell: ClampedCell },
    {
      Header: "slug",
      accessor: "slug",
      width: "20%",
      align: "left",
      Cell: ClampedCell,
      clampLines: 2,
    },
    {
      Header: "duration",
      accessor: "duration",
      width: "15%",
      align: "left",
      Cell: ClampedCell,
      clampLines: 2,
    },
    { Header: "price", accessor: "price", width: "15%", align: "left" },
    { Header: "status", accessor: "status", width: "15%", align: "center" },
    { Header: "actions", accessor: "actions", width: "10%", align: "center" },
  ];

  const rows = bootcamps.map((bootcamp) => ({
    title: bootcamp.title,
    slug: bootcamp.slug,
    duration: bootcamp.duration || "N/A",
    price: bootcamp.price ? `$${bootcamp.price}` : "N/A",
    status: (
      <MDBox ml={-1}>
        <span
          style={{
            padding: "4px 12px",
            borderRadius: "4px",
            fontSize: "0.75rem",
            fontWeight: "bold",
            color: "white",
            backgroundColor: bootcamp.active ? "#4caf50" : "#f44336",
          }}
        >
          {bootcamp.active ? "Active" : "Inactive"}
        </span>
      </MDBox>
    ),
    actions: (
      <IconButton onClick={(e) => handleMenuOpen(e, bootcamp)}>
        <Icon>more_vert</Icon>
      </IconButton>
    ),
  }));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                flexWrap="wrap"
                gap={1.5}
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  Bootcamps
                </MDTypography>
                <MDButton variant="contained" color="white" onClick={handleOpenDialog}>
                  Add Bootcamp
                </MDButton>
              </MDBox>

              {error && (
                <MDBox pt={2} px={2}>
                  <MDAlert color="error" dismissible onClose={() => setError("")}>
                    {error}
                  </MDAlert>
                </MDBox>
              )}

              {success && (
                <MDBox pt={2} px={2}>
                  <MDAlert color="success" dismissible onClose={() => setSuccess("")}>
                    {success}
                  </MDAlert>
                </MDBox>
              )}

              <MDBox pt={3}>
                {loading ? (
                  <MDBox p={3} textAlign="center">
                    <MDTypography variant="body2">Loading...</MDTypography>
                  </MDBox>
                ) : (
                  <DataTable
                    table={{ columns, rows }}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries={false}
                    noEndBorder
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        scroll="paper"
        fullScreen={fullScreenDialog}
      >
        <DialogTitle>{editMode ? "Edit Bootcamp" : "Add New Bootcamp"}</DialogTitle>
        <form onSubmit={handleSubmit} style={fullScreenDialog ? fullScreenForm : undefined}>
          <DialogContent>
            {dialogError && (
              <MDBox mb={2}>
                <MDAlert color="error" dismissible onClose={() => setDialogError("")}>
                  {dialogError}
                </MDAlert>
              </MDBox>
            )}
            <MDBox mb={2}>
              <MDInput
                type="text"
                label="Title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                fullWidth
                required
              />
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                type="text"
                label="Slug"
                name="slug"
                value={formData.slug}
                onChange={handleChange}
                fullWidth
                required
              />
            </MDBox>
            <MDBox mb={2}>
              <RichTextEditor
                label="Description"
                value={formData.description}
                onChange={(value) => setFormData((prev) => ({ ...prev, description: value }))}
                helperText="Provide detailed information about the bootcamp"
                height={300}
              />
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                type="text"
                label="Duration (e.g., 12 weeks)"
                name="duration"
                value={formData.duration}
                onChange={handleChange}
                fullWidth
              />
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                type="number"
                label="Price"
                name="price"
                value={formData.price}
                onChange={handleChange}
                fullWidth
              />
            </MDBox>
            <MDBox mb={2}>
              <ImageUpload
                currentImage={formData.image}
                onImageChange={(url) => setFormData((prev) => ({ ...prev, image: url }))}
                label="Bootcamp Image"
              />
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                type="date"
                label="Start Date"
                name="startDate"
                value={formData.startDate}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                type="number"
                label="Capacity"
                name="capacity"
                value={formData.capacity}
                onChange={handleChange}
                fullWidth
              />
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                type="number"
                label="Class Size"
                name="classSize"
                value={formData.classSize}
                onChange={handleChange}
                fullWidth
                helperText="Maximum students per class"
              />
            </MDBox>
            <MDBox mb={2}>
              <FormControl fullWidth>
                <InputLabel id="format-label">Format</InputLabel>
                <Select
                  labelId="format-label"
                  name="format"
                  value={formData.format}
                  label="Format"
                  onChange={handleChange}
                  required
                >
                  <MenuItem value="ONLINE">Online</MenuItem>
                  <MenuItem value="IN-PERSON">In-Person</MenuItem>
                  <MenuItem value="HYBRID">Hybrid</MenuItem>
                </Select>
              </FormControl>
            </MDBox>
            <MDBox mb={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.certificate}
                    onChange={handleChange}
                    name="certificate"
                    color="primary"
                  />
                }
                label="Certificate Provided"
              />
            </MDBox>
            <MDBox mb={2}>
              <MDTypography variant="caption" fontWeight="bold">
                Curriculum (one per line)
              </MDTypography>
              <MDInput
                multiline
                rows={5}
                placeholder="Enter curriculum items, one per line"
                value={formData.curriculum.join("\n")}
                onChange={(e) => {
                  const items = e.target.value.split("\n");
                  setFormData((prev) => ({ ...prev, curriculum: items }));
                }}
                fullWidth
              />
            </MDBox>
            <MDBox mb={2}>
              <MDTypography variant="caption" fontWeight="bold">
                Prerequisites & Requirements (one per line)
              </MDTypography>
              <MDInput
                multiline
                rows={4}
                placeholder="Enter prerequisites, one per line"
                value={formData.prerequisites.join("\n")}
                onChange={(e) => {
                  const items = e.target.value.split("\n");
                  setFormData((prev) => ({ ...prev, prerequisites: items }));
                }}
                fullWidth
              />
            </MDBox>
            <MDBox mb={2}>
              <MDTypography variant="caption" fontWeight="bold">
                What's Included (one per line)
              </MDTypography>
              <MDInput
                multiline
                rows={4}
                placeholder="Enter what's included, one per line (e.g., Resume & portfolio reviews)"
                value={formData.whatsIncluded.join("\n")}
                onChange={(e) => {
                  const items = e.target.value.split("\n");
                  setFormData((prev) => ({ ...prev, whatsIncluded: items }));
                }}
                fullWidth
              />
            </MDBox>
            <MDBox mb={2}>
              <MDTypography variant="caption" fontWeight="bold">
                Additional Support (one per line)
              </MDTypography>
              <MDInput
                multiline
                rows={3}
                placeholder="Enter additional support items, one per line (e.g., Teaching Assistants available)"
                value={formData.additionalSupport.join("\n")}
                onChange={(e) => {
                  const items = e.target.value.split("\n");
                  setFormData((prev) => ({ ...prev, additionalSupport: items }));
                }}
                fullWidth
              />
            </MDBox>
            <MDBox mb={2}>
              <MDTypography variant="h6" mb={1}>
                Instructors
              </MDTypography>
              {formData.instructors.map((instructor, index) => (
                <MDBox
                  key={index}
                  mb={2}
                  p={2}
                  sx={{
                    border: lightDark("1px solid #e0e0e0", `1px solid ${DARK.border}`),
                    borderRadius: "8px",
                  }}
                >
                  <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <MDTypography variant="caption" fontWeight="bold">
                      Instructor {index + 1}
                    </MDTypography>
                    <MDButton
                      size="small"
                      color="error"
                      sx={phoneTapHeight}
                      onClick={() => {
                        setFormData((prev) => {
                          const newInstructors = [...prev.instructors];
                          newInstructors.splice(index, 1);
                          return { ...prev, instructors: newInstructors };
                        });
                      }}
                    >
                      Remove
                    </MDButton>
                  </MDBox>
                  <MDBox mb={1}>
                    <MDInput
                      type="text"
                      label="Name"
                      value={instructor.name || ""}
                      onChange={(e) => {
                        setFormData((prev) => {
                          const newInstructors = [...prev.instructors];
                          newInstructors[index] = {
                            ...newInstructors[index],
                            name: e.target.value,
                          };
                          return { ...prev, instructors: newInstructors };
                        });
                      }}
                      fullWidth
                    />
                  </MDBox>
                  <MDBox mb={1}>
                    <MDInput
                      type="text"
                      label="Title"
                      value={instructor.title || ""}
                      onChange={(e) => {
                        setFormData((prev) => {
                          const newInstructors = [...prev.instructors];
                          newInstructors[index] = {
                            ...newInstructors[index],
                            title: e.target.value,
                          };
                          return { ...prev, instructors: newInstructors };
                        });
                      }}
                      fullWidth
                    />
                  </MDBox>
                  <MDBox mb={1}>
                    <MDInput
                      multiline
                      rows={2}
                      label="Bio"
                      value={instructor.bio || ""}
                      onChange={(e) => {
                        setFormData((prev) => {
                          const newInstructors = [...prev.instructors];
                          newInstructors[index] = { ...newInstructors[index], bio: e.target.value };
                          return { ...prev, instructors: newInstructors };
                        });
                      }}
                      fullWidth
                    />
                  </MDBox>
                  <MDBox mb={1}>
                    <MDInput
                      type="text"
                      label="Image URL"
                      value={instructor.image || ""}
                      onChange={(e) => {
                        setFormData((prev) => {
                          const newInstructors = [...prev.instructors];
                          newInstructors[index] = {
                            ...newInstructors[index],
                            image: e.target.value,
                          };
                          return { ...prev, instructors: newInstructors };
                        });
                      }}
                      fullWidth
                    />
                  </MDBox>
                  <MDBox>
                    <MDInput
                      type="text"
                      label="LinkedIn URL"
                      value={instructor.linkedIn || ""}
                      onChange={(e) => {
                        setFormData((prev) => {
                          const newInstructors = [...prev.instructors];
                          newInstructors[index] = {
                            ...newInstructors[index],
                            linkedIn: e.target.value,
                          };
                          return { ...prev, instructors: newInstructors };
                        });
                      }}
                      fullWidth
                    />
                  </MDBox>
                </MDBox>
              ))}
              <MDButton
                color="info"
                size="small"
                sx={phoneTapHeight}
                onClick={() => {
                  setFormData((prev) => ({
                    ...prev,
                    instructors: [
                      ...prev.instructors,
                      { name: "", title: "", bio: "", image: "", linkedIn: "" },
                    ],
                  }));
                }}
              >
                Add Instructor
              </MDButton>
            </MDBox>
            <MDBox mb={2}>
              <MDTypography variant="h6" mb={1}>
                Job Placement Statistics
              </MDTypography>
              <MDBox mb={2}>
                <MDInput
                  type="number"
                  label="Job Placement Rate (%)"
                  name="jobPlacementRate"
                  value={formData.jobPlacementRate}
                  onChange={handleChange}
                  fullWidth
                  inputProps={{ min: 0, max: 100, step: 0.1 }}
                />
              </MDBox>
              <MDBox mb={2}>
                <MDInput
                  type="number"
                  label="Average Starting Salary ($)"
                  name="averageStartingSalary"
                  value={formData.averageStartingSalary}
                  onChange={handleChange}
                  fullWidth
                />
              </MDBox>
              <MDBox mb={2}>
                <MDInput
                  type="text"
                  label="Average Time to Hire (e.g., 3 months)"
                  name="averageTimeToHire"
                  value={formData.averageTimeToHire}
                  onChange={handleChange}
                  fullWidth
                />
              </MDBox>
            </MDBox>
            <MDBox mb={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.active}
                    onChange={handleChange}
                    name="active"
                    color="primary"
                  />
                }
                label="Active"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.showOnMenu}
                    onChange={handleChange}
                    name="showOnMenu"
                    color="primary"
                  />
                }
                label="Show on menu"
              />
            </MDBox>
            <MDBox mb={2}>
              <FormControl fullWidth>
                <InputLabel id="level-label">Level</InputLabel>
                <Select
                  labelId="level-label"
                  name="level"
                  value={formData.level}
                  label="Level"
                  onChange={handleChange}
                  required
                >
                  <MenuItem value="BEGINNER">Beginner</MenuItem>
                  <MenuItem value="INTERMEDIATE">Intermediate</MenuItem>
                  <MenuItem value="ADVANCED">Advanced</MenuItem>
                </Select>
              </FormControl>
            </MDBox>
          </DialogContent>
          <DialogActions>
            <MDButton onClick={handleCloseDialog} color="secondary">
              Cancel
            </MDButton>
            <MDButton type="submit" color="info">
              {editMode ? "Update" : "Create"}
            </MDButton>
          </DialogActions>
        </form>
      </Dialog>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={() => handleEdit(selectedBootcamp)}>Edit</MenuItem>
        <MenuItem onClick={handleDelete}>Delete</MenuItem>
      </Menu>

      <Footer />
    </DashboardLayout>
  );
}

export default Bootcamps;
